import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { Link, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useSport } from "../contexts/SportContext"; 
import AccessKeyModal from "../components/AccessKeyModal"; 
import { useLocalWeather } from "../hooks/useLocalWeather";

/* ================= FlyIn mit Rotation & Scaling, stark lebendig ================= */
const directions45deg = [
  { x: -75, y: 0, rotate: -14 },
  { x: 75, y: 0, rotate: 12 },
  { x: 0, y: -75, rotate: 7 },
  { x: 0, y: 75, rotate: -9 },
  { x: -55, y: -55, rotate: -18 },
  { x: 55, y: 55, rotate: 16 },
  { x: -55, y: 55, rotate: 24 },
  { x: 55, y: -55, rotate: -22 },
];


const CUSTOM_SPORT_KEY = "gonkle_custom_sport";

const highlightSportKeys = [
  "football",
  "basketball",
  "volleyball",
  "tabletennis",
  "frisbee",
  "running",
  "cycling",
];

/* ================= Haupt Onboarding Seite ================= */
const OnboardingPage = () => {
  const { t, i18n } = useTranslation();
  const { showError } = useToast();
  const { startSession } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { sportTypes } = useSport();

  const [nickname, setNickname] = useState("");
  const [selfie, setSelfie] = useState(null);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAccessKeyModal, setShowAccessKeyModal] = useState(false);
  const [showSportCatalog, setShowSportCatalog] = useState(false);
  const [showSplashAd, setShowSplashAd] = useState(true);
  const [customSportKey, setCustomSportKey] = useState(() => {
    try {
      return localStorage.getItem(CUSTOM_SPORT_KEY) || "";
    } catch (error) {
      return "";
    }
  });

  const { weather, time, requestLocation } = useLocalWeather(
    process.env.REACT_APP_OPENWEATHER_KEY
  );

  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  useEffect(() => {
    const timer = setTimeout(() => setShowSplashAd(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (searchParams.get("key")) setShowAccessKeyModal(true);
  }, [searchParams]);

  // Input Validation Functions
  const validateNickname = (input) => {
    const trimmed = input.trim();
    
    // Länge: 3-24 Zeichen
    if (trimmed.length < 3) return { valid: false, message: t('nickname_too_short', 'Minimum 3 characters') };
    if (trimmed.length > 24) return { valid: false, message: t('nickname_too_long', 'Maximum 24 characters') };
    
    // Erlaubte Zeichen: Buchstaben, Zahlen, Bindestrich, Unterstrich
    const nicknameRegex = /^[a-zA-Z0-9\-_äöüßÄÖÜ]+$/;
    if (!nicknameRegex.test(trimmed)) {
      return { valid: false, message: t('nickname_invalid_chars', 'Only letters, numbers, - and _') };
    }
    
    return { valid: true, message: '' };
  };

  const validateSelfie = (file) => {
    if (!file) return { valid: false, message: t('selfie_required', 'Please take or upload a photo') };
    
    // Dateigröße: max 10MB (wichtig für kleinen VServer)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return { valid: false, message: t('file_too_large', 'Image must be under 10MB') };
    }
    
    // Bildformate akzeptieren: JPG, PNG, WebP, GIF
    const validMimes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/jpg'];
    if (!validMimes.includes(file.type)) {
      return { valid: false, message: t('invalid_image_format', 'Please use JPG, PNG, WebP or GIF') };
    }
    
    return { valid: true, message: '' };
  };

  const handleNicknameChange = (e) => {
    const input = e.target.value;
    setNickname(input);
    // Wenn Fehler existiert und Input korrigiert wird, Fehler löschen
    if (error && input.trim().length >= 3) {
      setError('');
    }
  };

  const handleSelfieChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validation = validateSelfie(file);
    if (!validation.valid) {
      setError(validation.message);
      setSelfie(null);
      return;
    }
    
    setSelfie(file);
    if (error) setError('');
  };

  const isReady = nickname.trim() && selfie;

  const handleSportSelection = async (sportKey) => {
    // Validiere Nickname
    const nicknameValidation = validateNickname(nickname);
    if (!nicknameValidation.valid) {
      setError(nicknameValidation.message);
      showError(nicknameValidation.message);
      return;
    }

    // Validiere Selfie
    const selfieValidation = validateSelfie(selfie);
    if (!selfieValidation.valid) {
      setError(selfieValidation.message);
      showError(selfieValidation.message);
      return;
    }

    if (!isReady || loading) return;
    setShowSportCatalog(false);
    setError("");
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("nickname", nickname.trim());
      formData.append("selfie", selfie);
      formData.append("favorite_sport", sportKey);
      await startSession(formData);
      // startSession already navigates to '/' and triggers the welcome overlay
      // via MapPage; nothing else to do here.
    } catch (err) {
      const msg = err?.toString() || "Unbekannter Fehler";
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  const containerStyle = {
    position: "relative", minHeight: "100vh",
    backgroundColor: "var(--color-background)",
    display: "flex", justifyContent: "center",
    alignItems: "center",
    padding: "2rem 1rem", userSelect: "none",
  };

  const cardStyle = {
    position: "relative", maxWidth: 420, margin: "auto", padding: "2rem 1.5rem",
    borderRadius: "var(--border-radius-large)", background: "var(--color-surface-elevated)",
    boxShadow: "var(--shadow-lg)", border: "1px solid var(--color-accent-dim)",
    color: "var(--color-text-primary)", textAlign: "center",
    width: "100%", fontFamily: "var(--font-display)", boxSizing: "border-box",
  };

  const highlightSports = useMemo(
    () => highlightSportKeys.map(key => sportTypes?.[key]).filter(Boolean),
    [sportTypes]
  );

  const customSport = customSportKey ? sportTypes?.[customSportKey] : null;

  const catalogSports = useMemo(
    () => Object.values(sportTypes || {}).sort((a, b) => a.name.localeCompare(b.name)),
    [sportTypes]
  );

  const gridItems = [...highlightSports, { key: "custom", isCustom: true }];

  const handleCustomSportSelect = (sportKey) => {
    setCustomSportKey(sportKey);
    try {
      localStorage.setItem(CUSTOM_SPORT_KEY, sportKey);
    } catch (error) {
      // Ignore storage errors
    }
    setShowSportCatalog(false);
    if (isReady && !loading) {
      handleSportSelection(sportKey);
    }
  };

  const gridButtonBase = {
    borderRadius: "var(--border-radius-medium)",
    border: "1px solid var(--color-accent-dim)",
    backgroundColor: "var(--color-surface)",
    color: "var(--color-text-primary)",
    padding: "14px 8px",
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontFamily: "var(--font-display)",
    transition: "all 0.15s ease",
  };

  const gridContent = (
    <div style={{ width: "100%" }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        gap: 10,
      }}>
        {gridItems.map(item => {
          if (item.isCustom) {
            const disabled = loading;
            return (
              <button
                key="grid-custom"
                type="button"
                onClick={() => {
                  if (customSport) {
                    handleSportSelection(customSport.key);
                  } else {
                    setShowSportCatalog(true);
                  }
                }}
                disabled={disabled}
                style={{
                  ...gridButtonBase,
                  borderStyle: "dashed",
                  backgroundColor: "rgba(99, 102, 241, 0.08)",
                  fontSize: 11,
                  letterSpacing: "0.04em",
                  position: "relative",
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
              >
                {customSport && (
                  <div
                    role="button"
                    tabIndex="0"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!loading) setShowSportCatalog(true);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.stopPropagation();
                        if (!loading) setShowSportCatalog(true);
                      }
                    }}
                    aria-label="Change sport"
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      border: "none",
                      background: "rgba(255, 255, 255, 0.92)",
                      color: "var(--color-text-primary)",
                      borderRadius: 6,
                      padding: "2px 6px",
                      fontSize: 10,
                      cursor: "pointer",
                    }}
                  >
                    ✎
                  </div>
                )}
                <span style={{ fontSize: 30 }}>{customSport ? customSport.icon : "➕"}</span>
                <span>{customSport ? customSport.name : "Pick"}</span>
              </button>
            );
          }
          const disabled = !isReady || loading;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => handleSportSelection(item.key)}
              disabled={disabled}
              style={{
                ...gridButtonBase,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
              }}
            >
              <span style={{ fontSize: 32 }}>{item.icon}</span>
              <span style={{ fontSize: 11, fontWeight: 600 }}>{item.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const overlayVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
  };

  const catalogButtonBase = {
    borderRadius: "var(--border-radius-medium)",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    backgroundColor: "rgba(99, 102, 241, 0.04)",
    color: "var(--color-text-primary)",
    padding: "10px",
    minHeight: 82,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 4,
    fontSize: 10,
  };

  const formElements = [
    { key: 'title', content: <h1 style={{ fontSize: "clamp(2.5rem, 8vw, 4rem)", marginBottom: 8, color: "var(--color-accent)", userSelect: "none", letterSpacing: "0.05em" }}>gonkle</h1> },
    { key: 'subtitle', content: <h2 style={{ fontWeight: "400", fontSize: "clamp(1.1rem, 3.5vw, 1.5rem)", marginBottom: 12, color: "var(--color-text-secondary)" }}>{t("app_subtitle")}</h2> },
    {
      key: 'nickname', content: <input type="text" placeholder={t("nickname_placeholder")} value={nickname} onChange={handleNicknameChange} required autoComplete="off" spellCheck={false} style={{
        width: "100%", maxWidth: 320, padding: "14px 18px", fontSize: 18, borderRadius: "var(--border-radius-medium)",
        border: "1px solid var(--color-accent-dim)", backgroundColor: "var(--color-surface)", color: "var(--color-text-primary)",
        textAlign: "center", outline: "none",
      }} />
    },
    {
      key: 'selfie', content: <label style={{
        display: "block", padding: 24, backgroundColor: "var(--color-surface)", borderRadius: "var(--border-radius-medium)",
        border: "2px dashed var(--color-accent-dim)", cursor: "pointer", userSelect: "none", color: "var(--color-text-secondary)",
        margin: "auto", fontWeight: "600", fontSize: "0.95rem", marginBottom: 10, maxWidth: 320,
      }}>{selfie ? `📸 ${selfie.name.substring(0, 20)}...` : t("selfie_placeholder")}
        <input data-testid="selfie-input" type="file" accept="image/*" capture="user" onChange={handleSelfieChange} style={{ display: "none" }} required />
      </label>
    },
    { key: 'grid', content: gridContent },
    {
      key: 'error', content: error ? <div style={{
        color: "var(--color-danger)", marginTop: 8, fontWeight: "600", fontSize: 13, userSelect: "text"
      }}>{error}</div> : null
    },
    {
      key: 'accessKey', content: <button type="button" onClick={() => setShowAccessKeyModal(true)} style={{
        background: 'none',
        border: 'none',
        padding: 0,
        fontFamily: 'inherit',
        cursor: "pointer",
        marginTop: 12,
        fontSize: 12,
        color: "var(--color-accent)",
        textDecoration: "underline"
      }}>{t("got_an_access_key_link")}</button>
    }
  ];


  return (
    <div style={containerStyle}>
      <AnimatePresence>
        {showSplashAd && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1400,
              backgroundColor: "#1a202c",
              backgroundImage: "url('/ads/cold-drink.jpeg')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        )}
      </AnimatePresence>

      <div
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 10,
          textAlign: "right",
          fontFamily: "var(--font-display)",
          color: "var(--color-text-primary)",
          background: "rgba(255, 255, 255, 0.92)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          borderRadius: 12,
          padding: "8px 10px",
          minWidth: 120,
        }}
      >
        <div style={{ fontSize: 12, opacity: 0.85 }}>
          {time.toLocaleTimeString(i18n.language, {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
        {weather && (
          <div style={{ fontSize: 12, fontWeight: 600 }}>
            {weather.temp}°C {weather.description}
          </div>
        )}
      </div>

      <form
        onSubmit={e => e.preventDefault()}
        noValidate
        style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 14, alignItems: "center" }}
      >
        {formElements.map(({ key, content }) =>
          content ? React.cloneElement(content, { key }) : null
        )}

        <AnimatePresence>
          {showSportCatalog && (
            <motion.div
              key="sport-catalog"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={overlayVariants}
              transition={{ type: "spring", stiffness: 200, damping: 25 }}
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 5,
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "var(--border-radius-large)",
                padding: "1.25rem",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                boxShadow: "var(--shadow-xl)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{t("all_sports_button")}</p>
                <button
                  type="button"
                  onClick={() => setShowSportCatalog(false)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "var(--color-text-primary)",
                    fontSize: 20,
                    cursor: "pointer",
                    padding: 0,
                  }}
                  aria-label={t("close_button")}
                >
                  ×
                </button>
              </div>
              <div style={{ flex: 1, overflowY: "auto" }}>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(84px, 1fr))",
                  gap: 10,
                }}>
                  {catalogSports.map(sport => {
                    const disabled = loading;
                    return (
                      <button
                        key={sport.key}
                        type="button"
                        onClick={() => handleCustomSportSelect(sport.key)}
                        disabled={disabled}
                        style={{
                          ...catalogButtonBase,
                          cursor: disabled ? "not-allowed" : "pointer",
                          opacity: disabled ? 0.55 : 1,
                        }}
                      >
                        <span style={{ fontSize: 30 }}>{sport.icon}</span>
                        <span style={{ fontSize: 11, fontWeight: 600 }}>{sport.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </form>

      <AnimatePresence>
        {showAccessKeyModal && <AccessKeyModal onClose={() => setShowAccessKeyModal(false)} />}
      </AnimatePresence>

      <motion.footer initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} style={{
        position: "fixed", bottom: 8, width: "100%", fontSize: 12, textAlign: "center",
        color: "var(--color-text-muted)", userSelect: "none", padding: 8,
        backgroundColor: "rgba(99, 102, 241, 0.1)", backdropFilter: "blur(3px)", fontFamily: "var(--font-display)"
      }}>
        <p style={{ margin: "0 0 4px 0" }}>
          <Link to="/impressum" style={{ color: "var(--color-text-muted)", userSelect: "none" }}>Impressum</Link> |{" "}
          <Link to="/datenschutz" style={{ color: "var(--color-text-muted)", userSelect: "none" }}>Datenschutz</Link>
        </p>
        <p style={{ margin: 0, fontSize: 11, color: "var(--color-text-muted)" }}>(c) nopainsoft</p>
      </motion.footer>
    </div>
  );
};

export default OnboardingPage;
